import { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import api from '../api/apiClient';
import { useAuth } from '../contexts/AuthContext';
import { tokenStore } from '../lib/tokenStore';

const FEATURES = [
  { title: 'Pay Rent Easily', body: 'Automated payments with BPAY, Visa and Mastercard.' },
  { title: 'Track Maintenance', body: 'Submit and monitor requests in real-time.' },
  { title: 'Manage Documents', body: 'All lease documents in one secure place.' },
];

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const response = await api.post<{ accessToken: string; refreshToken?: string }>('/auth/login', { email, password });
      if (response.refreshToken) tokenStore.setRefresh(response.refreshToken);
      const user = await login(response.accessToken);
      if (user) {
        navigate(`/${user.role.toLowerCase()}/dashboard`);
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left panel */}
      <div
        className="hidden lg:flex lg:w-[480px] flex-col justify-between px-12 py-10 flex-none"
        style={{ background: 'linear-gradient(160deg, var(--forest-900) 0%, var(--forest-700) 100%)' }}
      >
        <div>
          <div className="flex items-center gap-3 mb-12">
            <div
              className="w-9 h-9 rounded-10 font-bold text-lg flex items-center justify-center flex-none"
              style={{ background: 'var(--teal-400)', color: 'var(--forest-900)' }}
            >
              R
            </div>
            <span className="text-white font-semibold text-xl">Rentry</span>
          </div>

          <h1 className="text-4xl font-semibold text-white leading-tight mb-4">
            Rental management<br />made simple.
          </h1>
          <p className="text-white/70 text-[15px] leading-relaxed mb-10">
            One platform for tenants, landlords, agents and contractors — connected in real time.
          </p>

          <div className="space-y-5">
            {FEATURES.map(f => (
              <div key={f.title} className="flex items-start gap-3">
                <div
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-none mt-0.5"
                  style={{ background: 'var(--teal-400)' }}
                >
                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#0c4432" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
                    <path d="M5 12l5 5L20 7" />
                  </svg>
                </div>
                <div>
                  <p className="text-white font-medium text-[14px]">{f.title}</p>
                  <p className="text-white/65 text-[13px] mt-0.5">{f.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <p className="text-white/40 text-[11px]">© {new Date().getFullYear()} Rentry. All rights reserved.</p>
      </div>

      {/* Right panel */}
      <div className="flex-1 flex items-center justify-center px-6 bg-background">
        <div className="w-full max-w-sm">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2.5 mb-8">
            <div
              className="w-9 h-9 rounded-10 font-bold text-lg flex items-center justify-center"
              style={{ background: 'var(--forest-900)', color: 'var(--teal-400)' }}
            >
              R
            </div>
            <span className="font-semibold text-xl text-ink-900">Rentry</span>
          </div>

          <h2 className="text-[26px] font-semibold text-ink-900 mb-1">Sign in</h2>
          <p className="text-[13px] text-ink-500 mb-7">Enter your credentials to access your account.</p>

          {error && (
            <div className="mb-5 p-3 rounded-10 bg-danger-50 border border-red-200 text-[13px] text-danger-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-[12px] font-semibold text-ink-700 uppercase tracking-wider mb-1.5">
                Email
              </label>
              <input
                type="email" autoComplete="email" required
                value={email} onChange={e => setEmail(e.target.value)}
                className="w-full px-4 py-2.5 border border-ink-200 rounded-10 text-[14px] text-ink-900 placeholder-ink-400 bg-card focus:outline-none focus:ring-2 focus:ring-forest-900 focus:border-transparent"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label className="block text-[12px] font-semibold text-ink-700 uppercase tracking-wider mb-1.5">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'} autoComplete="current-password" required
                  value={password} onChange={e => setPassword(e.target.value)}
                  className="w-full px-4 py-2.5 pr-10 border border-ink-200 rounded-10 text-[14px] text-ink-900 placeholder-ink-400 bg-card focus:outline-none focus:ring-2 focus:ring-forest-900 focus:border-transparent"
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(v => !v)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3 text-ink-400 hover:text-ink-600"
                  tabIndex={-1}
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between py-1">
              <label className="flex items-center gap-2 text-[13px] text-ink-700 cursor-pointer">
                <input type="checkbox" className="w-4 h-4 rounded accent-forest-900" />
                Remember me
              </label>
              <a href="#" className="text-[13px] text-forest-900 font-medium hover:underline">
                Forgot password?
              </a>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 rounded-10 bg-forest-900 text-white font-semibold text-[14px] hover:bg-forest-800 disabled:opacity-50 transition-colors shadow-sm"
            >
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <div className="mt-5 text-center">
            <span className="text-[13px] text-ink-500">New to Rentry? </span>
            <button
              onClick={() => navigate('/register')}
              className="text-[13px] text-forest-900 font-semibold hover:underline"
            >
              Create an account
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
